import type {
  AuthResponse,
  GameServerInfo,
  LeaderboardEntry,
  PlayerCurrencies,
  PlayerProfile,
  RoomBrowserEntry,
  RoomConfig,
  RoomDetails,
  ShopItem,
  TowerType,
} from "@mmotdg/shared";
import { appState, clearSession } from "../state/AppState.js";

const BASE_URL = import.meta.env.VITE_AUTH_SERVER_URL ?? "http://localhost:3001";

interface RequestOpts {
  method?: string;
  body?: unknown;
  auth?: boolean;
}

async function apiCall<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers["content-type"] = "application/json";
  if (opts.auth !== false) {
    const token = appState.get().token;
    if (token) headers.authorization = `Bearer ${token}`;
  }
  const res = await fetch(`${BASE_URL}${path}`, {
    method: opts.method ?? "GET",
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  if (res.status === 401 && opts.auth !== false) {
    clearSession();
    throw new ApiError(401, "UNAUTHORIZED");
  }
  if (!res.ok) {
    let body: any = null;
    try {
      body = await res.json();
    } catch {
      // ignore
    }
    throw new ApiError(res.status, body?.code ?? `HTTP_${res.status}`, body);
  }
  return (await res.json()) as T;
}

export class ApiError extends Error {
  constructor(public status: number, public code: string, public body?: any) {
    super(code);
  }
}

export async function login(username: string, password: string): Promise<AuthResponse> {
  return apiCall<AuthResponse>("/auth/login", {
    method: "POST",
    body: { username, password },
    auth: false,
  });
}

export async function register(username: string, password: string): Promise<AuthResponse> {
  return apiCall<AuthResponse>("/auth/register", {
    method: "POST",
    body: { username, password },
    auth: false,
  });
}

export async function playAsGuest(): Promise<AuthResponse> {
  return apiCall<AuthResponse>("/auth/guest", { method: "POST", body: {}, auth: false });
}

export async function getMyProfile(): Promise<PlayerProfile> {
  return apiCall<PlayerProfile>("/profile/me");
}

export async function listServers(): Promise<{ servers: GameServerInfo[] }> {
  return apiCall<{ servers: GameServerInfo[] }>("/servers");
}

export async function joinQueue(mode: "arena" | "coop"): Promise<void> {
  await apiCall("/matchmaking/queue", { method: "POST", body: { mode } });
}

export async function leaveQueue(): Promise<void> {
  await apiCall("/matchmaking/queue", { method: "DELETE" });
}

export interface MatchmakingStatus {
  status: "waiting" | "matched";
  queueStats?: { arena: number; coop: number };
  assignment?: {
    roomId: string;
    mode: "arena" | "coop";
    serverId: string;
    serverHost: string;
    serverPort: number;
    players: Array<{ userId: string; username: string }>;
  };
}

export async function getQueueStatus(): Promise<MatchmakingStatus> {
  return apiCall<MatchmakingStatus>("/matchmaking/status");
}

export interface PracticeAssignment {
  roomId: string;
  mode: "practice";
  serverId: string;
  serverHost: string;
  serverPort: number;
  players: Array<{ userId: string; username: string }>;
  tutorialLevel: number | null;
}

export async function startPractice(
  tutorialLevel?: number,
): Promise<{ assignment: PracticeAssignment }> {
  return apiCall<{ assignment: PracticeAssignment }>("/practice/start", {
    method: "POST",
    body: tutorialLevel !== undefined ? { tutorialLevel } : {},
  });
}

export async function getLeaderboard(mode: "arena" | "coop" = "arena"): Promise<{ entries: LeaderboardEntry[] }> {
  return apiCall<{ entries: LeaderboardEntry[] }>(`/rankings?mode=${mode}`);
}

export async function listShopItems(): Promise<{ items: ShopItem[] }> {
  return apiCall<{ items: ShopItem[] }>("/shop/items");
}

export async function getCurrencies(): Promise<PlayerCurrencies> {
  return apiCall<PlayerCurrencies>("/shop/currencies");
}

export async function updateLoadout(loadout: TowerType[]): Promise<void> {
  await apiCall("/profile/me/loadout", { method: "PUT", body: { loadout } });
}

export interface FriendEntry {
  id: string;
  username: string;
  status: "pending" | "accepted" | "blocked";
}

export async function listFriends(): Promise<{ friends: FriendEntry[] }> {
  return apiCall<{ friends: FriendEntry[] }>("/friends");
}

export async function sendFriendRequest(username: string): Promise<void> {
  await apiCall("/friends/request", { method: "POST", body: { username } });
}

export async function acceptFriend(friendId: string): Promise<void> {
  await apiCall(`/friends/${friendId}/accept`, { method: "POST" });
}

export async function removeFriendApi(friendId: string): Promise<void> {
  await apiCall(`/friends/${friendId}`, { method: "DELETE" });
}

export async function presenceBatch(userIds: string[]): Promise<{ entries: import("@mmotdg/shared").PresenceEntry[] }> {
  return apiCall<{ entries: import("@mmotdg/shared").PresenceEntry[] }>("/presence/batch", {
    method: "POST",
    body: { userIds },
  });
}

export interface NotificationListResponse {
  items: import("@mmotdg/shared").Notification[];
  unread: number;
}

export async function listNotifications(): Promise<NotificationListResponse> {
  return apiCall<NotificationListResponse>("/notifications");
}

export async function markNotificationRead(id: string): Promise<void> {
  await apiCall(`/notifications/${id}/read`, { method: "POST" });
}

export async function markAllNotificationsRead(): Promise<void> {
  await apiCall("/notifications/read-all", { method: "POST" });
}

export interface LobbyStartResponse {
  room: RoomDetails;
  assignment: {
    roomId: string;
    mode: "arena" | "coop" | "boss_hunt" | "boss_endurance";
    serverId: string;
    serverHost: string;
    serverPort: number;
  };
}

export async function listLobbyRooms(): Promise<{ rooms: RoomBrowserEntry[] }> {
  return apiCall<{ rooms: RoomBrowserEntry[] }>("/lobby/rooms");
}

export async function createLobbyRoom(config: RoomConfig): Promise<{ room: RoomDetails }> {
  return apiCall<{ room: RoomDetails }>("/lobby/rooms", { method: "POST", body: config });
}

export async function getLobbyRoom(roomId: string): Promise<{ room: RoomDetails }> {
  return apiCall<{ room: RoomDetails }>(`/lobby/rooms/${roomId}`);
}

export async function getMyLobbyRoom(): Promise<{ room: RoomDetails | null }> {
  return apiCall<{ room: RoomDetails | null }>("/lobby/me");
}

export async function joinLobbyRoom(
  roomId: string,
  inviteCode?: string,
): Promise<{ room: RoomDetails }> {
  return apiCall<{ room: RoomDetails }>(`/lobby/rooms/${roomId}/join`, {
    method: "POST",
    body: inviteCode ? { inviteCode } : {},
  });
}

export async function leaveLobbyRoom(): Promise<void> {
  await apiCall("/lobby/leave", { method: "POST" });
}

export async function setLobbyReady(
  roomId: string,
  isReady: boolean,
): Promise<{ room: RoomDetails }> {
  return apiCall<{ room: RoomDetails }>(`/lobby/rooms/${roomId}/ready`, {
    method: "POST",
    body: { isReady },
  });
}

export async function kickLobbyPlayer(
  roomId: string,
  userId: string,
): Promise<{ room: RoomDetails }> {
  return apiCall<{ room: RoomDetails }>(`/lobby/rooms/${roomId}/kick`, {
    method: "POST",
    body: { userId },
  });
}

export async function startLobbyRoom(roomId: string): Promise<LobbyStartResponse> {
  return apiCall<LobbyStartResponse>(`/lobby/rooms/${roomId}/start`, { method: "POST" });
}
